import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { formatCustomDate, formatDateToLexicographicalOrder, updateYAxisTitle } from '../AnalyticsHelper';
import { DEFAULT_HEIGHT, PT2H } from '../AnalyticsConstants';
import dayjs from 'utils/dayjs';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import useChartTheme from './useChartTheme';

type ColumnChartProps = {
  data: {
    name: string;
    data: {
      x: string;
      y: number;
    }[];
  }[];
  metricName?: string;
  xAxis?: string[];
  granularity?: string;
  timezone: string;
  colors?: string[];
}

const ColumnChart = React.memo(({ data, metricName, xAxis, granularity, timezone, colors }: ColumnChartProps) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const chartTheme = useChartTheme();

  React.useEffect(() => {
    setIsLoading(true);
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 10);

    return () => clearTimeout(timer);
  }, [data, xAxis]);

  const chartOptions: any = React.useMemo(() => ({
    chart: {
      toolbar: { show: false },
      foreColor: chartTheme.foreColor,
    },
    grid: { borderColor: chartTheme.gridBorder },
    tooltip: { theme: chartTheme.mode },
    xaxis: { 
      type: 'category', 
      categories: xAxis,
      labels: { 
        show: true,
        formatter: function (value:any) {
          const date = new Date(value);
          if (granularity === PT2H) {
            return formatCustomDate(value);
          } else {
            return formatDateToLexicographicalOrder(date, timezone);
          }
        },
      }, 
    },
    colors: colors || [],
    plotOptions: { 
      bar: { 
        borderRadius: 0, 
        columnWidth: '40%', 
        dataLabels: { position: 'top' }, 
      }, 
    },
    yaxis: { 
      opposite: false, 
      title: { 
        text: updateYAxisTitle(metricName ?? ""), 
        rotate: -90, 
        offsetX: 3, 
        offsetY: 0, 
        style: {
          color: chartTheme.axisTitleColor,
          fontSize: '11px',
          fontFamily: 'Noto Sans, sans-serif',
          fontWeight: 700,
          cssClass: 'apexcharts-yaxis-title',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val:any) { return val + "%"; },
      offsetY: -20,
      style: { fontSize: '8px', colors: ["transparent"] }
    },
  }), [colors, granularity, metricName, xAxis, timezone, chartTheme]);

  const series = React.useMemo(() => {
    return data.map((entry, index) => ({
      name: entry.name,
      data: xAxis?.map((dateString) => {
        const categoryDate = dayjs.utc(dateString);
        const dataPoint = entry.data.find((point) => {
          const pointDate = dayjs.utc(point.x);
          return pointDate.isSame(categoryDate);
        });
        return dataPoint ? dataPoint.y : null;
      }),
      strokeDashArray: index === 0 ? undefined : '5',
    }));
  }, [data, xAxis]);

  if (isLoading) {
    return (
      <div className='tw-justify-center tw-flex tw-text-center tw-items-center' style={{ height: DEFAULT_HEIGHT }}>
        <Spinner size="lg" label="Loading chart" showLabel data-testid="columnchart-loading" />
      </div>
    );
  }

  return (
    <div style={{ height: DEFAULT_HEIGHT }}>
      <ReactApexChart
        options={chartOptions}
        series={series as any}
        type="bar"
        height={DEFAULT_HEIGHT}
        width="100%"
      />
    </div>
  );
});

ColumnChart.displayName = 'ColumnChart';

export default ColumnChart;